// The locked editor's state: the song (with undo/redo), the period it is locked to, which of its
// patterns is being edited, view and input settings. Every change is pushed to the engine and
// saved as a local draft.
//
// Editing tools work on one 8-bar pattern at a time through a lens: they see the song with the
// current pattern's notes in `events` (`patternView`), and their result is written back into
// that pattern (`edit`). Mix, sounds and arrangement are song-wide.
import { useMemo } from "react";
import { create } from "zustand";
import {
  defaultMix,
  relativeKey,
  songKey,
  type Key,
  emptyLoop,
  GRID_TICKS,
  sanitizeLoop,
  type Arrangement,
  type GridDivision,
  type LoopData,
  type PeriodDef,
  type PeriodSpec,
  patternCount,
  patternEvents,
  withPattern,
} from "@eb/shared";
import { begin, canRedo, canUndo, commit, createHistory, end, redo, undo, type History } from "./history";
import type { LaneId } from "./gridMath";

export interface ServerDraftMeta {
  id: string;
  status: "draft" | "checking" | "review" | "approved" | "rejected";
  reservedUntil: number | null;
  loopId: string | null;
  wizardShare: number | null;
  playsAt: number | null;
  check: { passed: boolean; results: { rule: string; passed: boolean; reason: string }[]; at: number } | null;
  sentOn?: boolean;
}

export type PlayMode = "stopped" | "loop" | "song" | "recording";

export interface EditorState {
  draftId: string | null;
  def: PeriodDef | null;
  spec: PeriodSpec | null;
  hist: History<LoopData>;
  /** The pattern being edited (0 = A … 3 = D). */
  pattern: number;
  title: string;
  grid: GridDivision;
  quantizeOnRecord: boolean;
  quantizeStrength: number;
  /** What the 16 pads play: 0 = drums, 16/17/18 = an instrument (scale notes). */
  padTarget: number;
  /** Octave shift for instrument pads (−2…2). */
  padOctave: number;
  zoomBars: 1 | 2 | 4;
  scrollBar: number;
  collapsed: LaneId[];
  selectedSlot: number;
  play: PlayMode;
  countIn: boolean;
  noteRepeat: boolean;
  repeatGrid: GridDivision;
  savedAt: number | null;
  /** Unsaved changes pending. */
  dirty: boolean;
  /** Approved or being checked: no edits. */
  readOnly: boolean;
  /** Bumped when a draft is loaded, so loading isn't mistaken for an edit (no autosave). */
  loadSeq: number;
  /** Server-side draft state (null for a local, signed-out draft). */
  server: ServerDraftMeta | null;
  set: (p: Partial<Omit<EditorState, "set">>) => void;
}

export const useEditor = create<EditorState>((set) => ({
  draftId: null,
  def: null,
  spec: null,
  hist: createHistory<LoopData>({ events: [], mix: [] }),
  pattern: 0,
  title: "",
  grid: "1/16",
  quantizeOnRecord: true,
  quantizeStrength: 1,
  padTarget: 0,
  padOctave: 0,
  zoomBars: 4,
  scrollBar: 0,
  collapsed: [],
  selectedSlot: 0,
  play: "stopped",
  countIn: true,
  noteRepeat: false,
  repeatGrid: "1/16",
  savedAt: null,
  dirty: false,
  readOnly: false,
  server: null,
  loadSeq: 0,
  set: (p) => set(p),
}));

export const gridTicks = () => GRID_TICKS[useEditor.getState().grid];

export function loadIntoEditor(
  draftId: string,
  def: PeriodDef,
  spec: PeriodSpec,
  loop: LoopData | null,
  title = "",
): void {
  const l = loop ? sanitizeLoop(loop, def.key, defaultMix(spec)) : emptyLoop(spec);
  useEditor.setState({
    draftId,
    def,
    spec,
    hist: createHistory(l),
    pattern: 0,
    title,
    play: "stopped",
    savedAt: null,
    dirty: false,
    readOnly: false,
    server: null,
    // Loading isn't an edit: the autosave ignores this change.
    loadSeq: useEditor.getState().loadSeq + 1,
  });
}

const views = new WeakMap<LoopData, LoopData[]>();

/** The song as the editing tools see it: pattern `i`'s notes in `events` (cached, stable). */
export function patternView(song: LoopData, i: number): LoopData {
  if (i === 0) return song;
  let cache = views.get(song);
  if (!cache) views.set(song, (cache = []));
  return (cache[i] ??= { ...song, events: patternEvents(song, i) });
}

/** Write a tool's result on pattern `i`'s view back into the song. */
export function mergeView(song: LoopData, i: number, view: LoopData): LoopData {
  if (i === 0) return { ...view, extra_patterns: song.extra_patterns, arrangement: song.arrangement };
  return withPattern(
    { ...view, events: song.events, extra_patterns: song.extra_patterns, arrangement: song.arrangement },
    i,
    view.events,
  );
}

/** The current pattern's view of the song. */
export function currentView(): LoopData {
  const s = useEditor.getState();
  return patternView(s.hist.present, s.pattern);
}

/** The key the song is in: the day's, or its relative when the song chose the second key. */
export function songKeyOf(s: Pick<EditorState, "def" | "hist">): Key | null {
  return s.def ? songKey(s.def.key, s.hist.present) : null;
}

/** Hook: the song's key (re-renders when the day or the song's choice changes). */
export function useSongKey(): Key | null {
  const def = useEditor((s) => s.def);
  const alt = useEditor((s) => s.hist.present.alt_key === true);
  return useMemo(() => (def ? (alt ? relativeKey(def.key) : def.key) : null), [def, alt]);
}

/** Choose the song's key: false = the day's key, true = its relative. */
export function setAltKey(alt: boolean): void {
  editSong((l) => {
    if ((l.alt_key === true) === alt) return l;
    const { alt_key: _, ...rest } = l;
    return alt ? { ...rest, alt_key: true } : rest;
  });
}

/** Hook: the current pattern's view (re-renders on edits and pattern changes). */
export function useView(): LoopData {
  return useEditor((s) => patternView(s.hist.present, s.pattern));
}

/** Apply an edit to the current pattern (one undo step unless a transaction is open). */
export function edit(fn: (l: LoopData) => LoopData): void {
  const s = useEditor.getState();
  if (s.readOnly) return;
  const view = patternView(s.hist.present, s.pattern);
  const next = fn(view);
  if (next !== view) useEditor.setState({ hist: commit(s.hist, mergeView(s.hist.present, s.pattern, next)) });
}

/** Apply a song-wide edit (patterns, arrangement). */
export function editSong(fn: (l: LoopData) => LoopData): void {
  const s = useEditor.getState();
  if (s.readOnly) return;
  const next = fn(s.hist.present);
  if (next !== s.hist.present) {
    const pattern = Math.min(s.pattern, patternCount(next) - 1);
    useEditor.setState({ hist: commit(s.hist, next), pattern });
  }
}

export function beginGesture(label: string): void {
  useEditor.setState((s) => ({ hist: begin(s.hist, label) }));
}

export function endGesture(): void {
  useEditor.setState((s) => ({ hist: end(s.hist) }));
}

export function undoEdit(): void {
  useEditor.setState((s) => {
    const hist = undo(s.hist);
    return { hist, pattern: Math.min(s.pattern, patternCount(hist.present) - 1) };
  });
}

export function redoEdit(): void {
  useEditor.setState((s) => {
    const hist = redo(s.hist);
    return { hist, pattern: Math.min(s.pattern, patternCount(hist.present) - 1) };
  });
}

export const canUndoNow = () => canUndo(useEditor.getState().hist);
export const canRedoNow = () => canRedo(useEditor.getState().hist);

export function currentLoop(): LoopData {
  return useEditor.getState().hist.present;
}

export function arrangementFor(mode: PlayMode, pattern = 0): Arrangement {
  if (mode === "song") return { type: "song" };
  return { type: "loop", pattern };
}
